import { asImageSrc } from '@prismicio/client';

import type { Content, ImageFieldImage } from '@prismicio/client';

/**
 * Brand assets, SEO defaults and the favicon, already resolved to URLs. Every
 * field has a bundled fallback under `public/`, so the app is fully branded
 * before a `site_settings` document exists.
 */
export type SiteSettings = {
  homeLogoUrl: string;
  homeBackgroundUrl: string;
  /** The full-colour lockup on the white header bar (`AppHeader`). */
  headerLogoUrl: string;
  /** The white lockup at the top of the age and name screens, over the photograph. */
  onboardingLogoUrl: string;
  /** The mark above the heading on the age and name screens. */
  stepLogoUrl: string;
  secondaryBackgroundUrl: string;
  /**
   * The partner line under the step logo. `null` when turned off in Prismic.
   * A `null` label falls back to the translated catalogue string.
   */
  partner: { label: string | null; logoUrl: string; logoHeight: number | null } | null;
  /**
   * Behind the module conversation, with a blue wash over it so the thread
   * stays readable whatever the photograph is (`ChatGround`).
   *
   * The one background that is genuinely optional, and so the one typed
   * `string | null`: with no asset the chat keeps its built-in per-theme
   * backdrops — sand for kids, ocean for everyone else — which is a different
   * thing from a bundled fallback file, because the choice is per theme.
   */
  chatBackgroundUrl: string | null;
  /** The coach avatar on the teen and adult courses. */
  coachAvatarUrl: string;
  /**
   * The kids course's coach, a Kangaroo Beach lifeguard. The avatar is a
   * character bursting out of a round badge, drawn uncropped so the head rises
   * above the circle; editors can swap the character (Sandy, Bondi…) here.
   */
  kidsCoach: { name: string; avatarUrl: string };
  /** Logo heights in pixels, clamped to each layout. `null` keeps the responsive default. */
  logoHeights: { header: number | null; home: number | null; step: number | null };
  /**
   * A `null` template uses the bundled file for that theme. The text is English
   * only because Bebas Neue has no Arabic, Devanagari or CJK glyphs.
   */
  certificate: {
    adultTemplateUrl: string | null;
    kidsTemplateUrl: string | null;
    text: string;
    /** `#rrggbb`. The date after the text stays black. */
    textColour: string;
  };
  faviconUrl: string;
  seo: {
    defaultTitle: string;
    /** `%s` is replaced by the page's own title. */
    titleTemplate: string;
    defaultDescription: string;
    ogImageUrl: string;
    /** Absolute origin used for canonical URLs, sitemap and robots. */
    canonicalBase: string | null;
  };
};

/** Used verbatim when no document exists, and field by field when one does. */
export const siteSettingsFallback: SiteSettings = {
  homeLogoUrl: '/images/logo.webp',
  homeBackgroundUrl: '/images/splash-background.webp',
  // The 818x204 SLS + Qantas lockup in two colourways: navy wordmark and the
  // positive Qantas mark for the white bar, white artwork for the photograph.
  // (`logo-horizontal.webp` is misnamed: it is a 377x569 portrait asset.)
  headerLogoUrl: '/images/logo-app-positive.webp',
  onboardingLogoUrl: '/images/logo-app.webp',
  // The roundel, not a lockup: it reads on the step screens' light frosted card
  // without depending on which colourway the lockup is.
  stepLogoUrl: '/images/slsa-logo.webp',
  secondaryBackgroundUrl: '/images/ocean-background.webp',
  partner: { label: null, logoUrl: '/images/partner-qantas.webp', logoHeight: null },
  // Unset on purpose: see `chatBackgroundUrl` above.
  chatBackgroundUrl: null,
  coachAvatarUrl: '/images/coach-lifesaver.svg',
  kidsCoach: { name: 'Sandy', avatarUrl: '/images/surfer-icon-child.webp' },
  logoHeights: { header: null, home: null, step: null },
  certificate: {
    adultTemplateUrl: null,
    kidsTemplateUrl: null,
    text: 'You have completed the Beach Passport on',
    textColour: '#006DB8',
  },
  faviconUrl: '/static/favicons/favicon-96x96.png',
  seo: {
    defaultTitle: 'Beach Passport',
    titleTemplate: '%s · Beach Passport',
    defaultDescription: 'Learn to stay safe at the beach and earn your Beach Passport.',
    ogImageUrl: '/images/beach-passport-logo.png',
    canonicalBase: null,
  },
};

const text = (value: string | null | undefined, fallback: string) => {
  const trimmed = value?.trim();
  return trimmed && trimmed !== '' ? trimmed : fallback;
};

const image = (field: ImageFieldImage | null | undefined, fallback: string) => asImageSrc(field) ?? fallback;

/** An image field that may legitimately be empty — `null` means "not set". */
const optionalImage = (field: ImageFieldImage | null | undefined) => asImageSrc(field) ?? null;

/** Rounds and clamps an editor's pixel size. `null` when unset. */
const pixels = (value: number | null | undefined, min: number, max: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : null;

/** Accepts a `#rrggbb` colour, otherwise returns the fallback. */
const colour = (value: string | null | undefined, fallback: string) =>
  value && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;

/** Merges a `site_settings` document over the bundled fallbacks, field by field. */
export function resolveSiteSettings(doc: Content.SiteSettingsDocument | null): SiteSettings {
  if (!doc) {
    return siteSettingsFallback;
  }

  const { data } = doc;
  const { seo } = siteSettingsFallback;

  return {
    homeLogoUrl: image(data.home_logo, siteSettingsFallback.homeLogoUrl),
    homeBackgroundUrl: image(data.home_background, siteSettingsFallback.homeBackgroundUrl),
    headerLogoUrl: image(data.header_logo, siteSettingsFallback.headerLogoUrl),
    onboardingLogoUrl: image(data.profile_logo, siteSettingsFallback.onboardingLogoUrl),
    stepLogoUrl: image(data.step_logo, siteSettingsFallback.stepLogoUrl),
    secondaryBackgroundUrl: image(data.secondary_background, siteSettingsFallback.secondaryBackgroundUrl),
    // Only an explicit `false` hides the partner. An unset toggle shows it.
    partner:
      data.show_partner === false
        ? null
        : {
            label: data.partner_label?.trim() ? data.partner_label.trim() : null,
            logoUrl: image(data.partner_logo, siteSettingsFallback.partner?.logoUrl ?? ''),
            logoHeight: pixels(data.partner_logo_height, 16, 48),
          },
    chatBackgroundUrl: optionalImage(data.chat_background),
    coachAvatarUrl: image(data.coach_avatar, siteSettingsFallback.coachAvatarUrl),
    kidsCoach: {
      name: text(data.kids_coach_name, siteSettingsFallback.kidsCoach.name),
      avatarUrl: image(data.kids_coach_avatar, siteSettingsFallback.kidsCoach.avatarUrl),
    },
    // Past these bounds the header wraps, the home logo pushes Start off a
    // phone, or the step card overflows.
    logoHeights: {
      header: pixels(data.header_logo_height, 24, 56),
      home: pixels(data.home_logo_height, 80, 320),
      step: pixels(data.step_logo_height, 32, 128),
    },
    certificate: {
      adultTemplateUrl: optionalImage(data.certificate_template_adult),
      kidsTemplateUrl: optionalImage(data.certificate_template_kids),
      text: text(data.certificate_text, siteSettingsFallback.certificate.text),
      textColour: colour(data.certificate_text_colour, siteSettingsFallback.certificate.textColour),
    },
    faviconUrl: image(data.favicon, siteSettingsFallback.faviconUrl),
    seo: {
      defaultTitle: text(data.default_title, seo.defaultTitle),
      titleTemplate: text(data.title_template, seo.titleTemplate),
      defaultDescription: text(data.default_description, seo.defaultDescription),
      ogImageUrl: image(data.og_image, seo.ogImageUrl),
      canonicalBase: text(data.canonical_base, '').replace(/\/+$/, '') || null,
    },
  };
}
